import React, { FC, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    NewFeaturesTourStepType,
    NewFeaturesTourType,
    finishTour,
    getFeatureTourFromStorage,
    nextStep
} from '../../../../../reducers/new-features-tour';
import { getFeatureTourPosition, modalWidth } from './feature-tour-step.helpers';
import './feature-tour-step.scss';

const FeatureTourStep: FC = () => {
    const dispatch = useDispatch();
    const newFeaturesTours: NewFeaturesTourType[] = useSelector((state: RootState) => state.main.newFeaturesTour);
    const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);

    const activeTours = newFeaturesTours.filter(
        (tour) => !tour.isFinished && (document.querySelector(tour.steps[0].selector) as HTMLElement)
    );
    const { currentStep, steps, name } = activeTours[0]
        ? activeTours[0]
        : ({ currentStep: -1, steps: [], name: '' } as unknown as NewFeaturesTourType);
    const currentTourStep: NewFeaturesTourStepType | undefined = steps[currentStep];
    const targetElement = document.querySelector(currentTourStep?.selector) as HTMLElement;
    const stepLength = activeTours.reduce((length, tour) => length + tour.steps.length, 0);
    const [stepsCount, setStepsCount] = useState<number>(0);
    const [tourStep, setTourStep] = useState<number>(1);
    const isLastOne = tourStep === stepsCount;

    useLayoutEffect(() => {
        dispatch(getFeatureTourFromStorage());
    }, []);

    useEffect(() => {
        if (stepLength) {
            setStepsCount((prev) => (prev > stepLength ? prev : stepLength));
        } else {
            setStepsCount(0);
            setTourStep(1);
        }
    }, [stepLength]);

    useEffect(() => {
        const targetElement = document.querySelector(currentTourStep?.selector ?? undefined) as HTMLDivElement;

        if (!targetElement) {
            return;
        }

        const updateModalPosition = () => {
            if (targetElement) {
                document.body.style.overflow = 'hidden';

                setModalPosition(getFeatureTourPosition(targetElement));
            }
        };

        updateModalPosition();
        window.addEventListener('resize', updateModalPosition);
        window.addEventListener('scroll', updateModalPosition);
        targetElement.classList.add('feature-tour-active');

        return () => {
            window.removeEventListener('resize', updateModalPosition);
            window.removeEventListener('scroll', updateModalPosition);
            targetElement.classList.remove('feature-tour-active');
            document.body.style.overflow = '';
        };
    }, [currentTourStep?.selector]);

    useEffect(() => {
        const targetElement = document.querySelector(currentTourStep?.selector ?? undefined) as HTMLDivElement;

        if (!targetElement || !currentTourStep.isClickable) {
            return;
        }

        const handleClick = (event: Event) => {
            if (!event.defaultPrevented) {
                if (!isLastOne) {
                    event.stopPropagation();
                    event.preventDefault();
                }
                targetElement.onclick = onNextClick;
            }
            onNextClick();
        };

        targetElement.addEventListener('click', handleClick);

        return () => {
            targetElement.removeEventListener('click', handleClick);
        };
    }, [currentTourStep?.selector, isLastOne]);

    const onNextClick = () => {
        if (currentStep + 1 >= steps.length) {
            dispatch(finishTour(name));
        } else {
            dispatch(nextStep(name));
        }
        setTourStep((prev) => prev + 1);
    };

    if (!currentTourStep || !targetElement || !modalPosition) {
        return null;
    }

    return (
        <div className='feature-tour'>
            <div className='feature-tour-bg' />
            <div
                className='feature-tour-step'
                style={{
                    width: modalWidth,
                    top: modalPosition.top,
                    left: modalPosition.left
                }}
            >
                <div className='feature-tour-counter'>
                    {tourStep}/{stepsCount}
                </div>
                <div className='description'>{currentTourStep.text}</div>
                <div className='button-container'>
                    <button onClick={onNextClick} className='next-button'>
                        {isLastOne ? 'Alles klar' : 'Nächste'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeatureTourStep;
